// ============================================
// components/ModalVisualizarDispensacao.jsx
// Modal para visualizar detalhes de uma dispensação
// Design Glassmorphism Premium - Scriptus Pharma Partner
// ============================================

import React from "react";
import { createPortal } from "react-dom";
import { 
  X, 
  User,
  Pill,
  FileText,
  Clock,
  Download,
  Calendar,
  CheckCircle,
  Shield,
  Package,
  Stethoscope,
  Hash
} from "lucide-react";

const ModalVisualizarDispensacao = ({ 
  visivel, 
  dispensacao, 
  onFechar,
  onDownloadXml
}) => {
  // Não renderiza se não estiver visível ou sem dispensação
  if (!visivel || !dispensacao) return null;

  // Contador de itens
  const contarItens = () => {
    return dispensacao.items?.length || dispensacao.itemsCount || 0;
  };

  // Fechar modal ao clicar no backdrop
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onFechar();
    }
  };

  // Formatar data compatível com múltiplos formatos
  const formatarData = (data) => {
    if (!data) return "Não disponível";
    
    try {
      if (data && data.seconds) {
        return new Date(data.seconds * 1000).toLocaleDateString("pt-BR");
      }
      else if (data.toDate && typeof data.toDate === 'function') {
        return data.toDate().toLocaleDateString("pt-BR");
      }
      else if (data instanceof Date) {
        return data.toLocaleDateString("pt-BR");
      }
      else if (typeof data === 'string') {
        return new Date(data).toLocaleDateString("pt-BR");
      }
    } catch (error) {
      console.warn('Erro ao formatar data:', error);
    }
    
    return "Não disponível";
  };

  // Formatar hora compatível com múltiplos formatos
  const formatarHora = (data) => {
    if (!data) return "--:--";
    
    try {
      if (data && data.seconds) {
        return new Date(data.seconds * 1000).toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit"
        });
      }
      else if (data.toDate && typeof data.toDate === 'function') {
        return data.toDate().toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit"
        });
      }
      else if (data instanceof Date) {
        return data.toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit"
        });
      }
      else if (typeof data === 'string') {
        return new Date(data).toLocaleTimeString("pt-BR", {
          hour: "2-digit",
          minute: "2-digit"
        });
      }
    } catch (error) {
      console.warn('Erro ao formatar hora:', error);
    }
    
    return "--:--";
  };

  // Handler para download
  const handleDownload = () => {
    if (onDownloadXml && dispensacao.prescriptionId) {
      onDownloadXml(dispensacao.prescriptionId);
    }
  };

  // Obter nome do paciente
  const getNomePaciente = () => {
    return dispensacao.patient || dispensacao.patientName || "Paciente não informado";
  };

  // Obter CPF do paciente
  const getCpfPaciente = () => {
    return dispensacao.patientCpf || dispensacao.cpf || null;
  };

  // Obter nome do médico
  const getNomeMedico = () => {
    return dispensacao.doctor || dispensacao.doctorName || null;
  };

  // Obter CRM do médico
  const getCrmMedico = () => {
    return dispensacao.doctorCrm || dispensacao.crm || null;
  };

  return createPortal(
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4"
      onClick={handleBackdropClick}
      style={{
        background: 'rgba(0, 0, 0, 0.4)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
      }}
    >
      {/* Modal Container */}
      <div 
        className="relative w-full max-w-2xl max-h-[95vh] sm:max-h-[90vh] flex flex-col glass-card rounded-2xl sm:rounded-3xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header Compacto */}
        <div className="p-4 sm:p-5 flex-shrink-0 border-b border-gray-200/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3 min-w-0 flex-1">
              <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-xl flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 shadow-sm">
                {getNomePaciente().charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-base sm:text-lg sf-pro-display font-semibold text-gray-900 tracking-tight truncate">
                  {getNomePaciente()}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  <span className="glass-badge bg-emerald-100/70 text-emerald-700 border-emerald-200/50 flex items-center space-x-1">
                    <CheckCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                    <span className="sf-pro-text font-medium">Dispensado</span>
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={onFechar}
              className="w-8 h-8 sm:w-9 sm:h-9 bg-red-100/80 hover:bg-red-200/90 text-red-600 hover:text-red-700 rounded-lg border border-red-200/50 hover:border-red-300/60 transition-all duration-300 flex items-center justify-center shadow-sm hover:shadow-md transform hover:scale-105 flex-shrink-0 ml-3"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Conteúdo Principal */}
        <div className="flex-1 overflow-y-auto custom-scrollbar">
          
          {/* Cards de Informações Básicas */}
          <div className="p-4 sm:p-5 border-b border-gray-200/50">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              
              {/* Data */}
              <div className="glass-card-compact rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-1">
                  <Calendar className="w-3.5 h-3.5 text-gray-600" />
                  <span className="text-xs sf-pro-text font-medium text-gray-600">Data</span>
                </div>
                <p className="text-xs sf-pro-text font-semibold text-gray-900">
                  {formatarData(dispensacao.dispensedAt)}
                </p>
              </div>

              {/* Horário */}
              <div className="glass-card-compact rounded-lg p-3">
                <div className="flex items-center space-x-2 mb-1">
                  <Clock className="w-3.5 h-3.5 text-gray-600" />
                  <span className="text-xs sf-pro-text font-medium text-gray-600">Horário</span>
                </div>
                <p className="text-xs sf-pro-text font-semibold text-gray-900">
                  {formatarHora(dispensacao.dispensedAt)}
                </p>
              </div>

              {/* Itens */}
              <div className="glass-card-compact rounded-lg p-3 col-span-2 sm:col-span-1">
                <div className="flex items-center space-x-2 mb-1">
                  <Package className="w-3.5 h-3.5 text-gray-600" />
                  <span className="text-xs sf-pro-text font-medium text-gray-600">Itens</span>
                </div>
                <p className="text-xs sf-pro-text font-semibold text-gray-900">
                  {contarItens()} medicamento(s)
                </p>
              </div>
            </div>
          </div>

          {/* Dados do Paciente */}
          <div className="p-4 sm:p-5 border-b border-gray-200/50">
            <h3 className="sf-pro-display font-semibold text-gray-900 mb-3 flex items-center space-x-2 text-sm">
              <User className="w-4 h-4" />
              <span>Dados do Paciente</span>
            </h3>
            <div className="glass-card-compact rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="space-y-2">
                <div className="flex justify-between items-start">
                  <span className="text-xs sf-pro-text text-gray-600">Nome:</span>
                  <span className="text-xs sf-pro-text font-semibold text-gray-900 text-right max-w-[60%] truncate">
                    {getNomePaciente()}
                  </span>
                </div>
                {getCpfPaciente() && (
                  <div className="flex justify-between items-center">
                    <span className="text-xs sf-pro-text text-gray-600">CPF:</span>
                    <span className="text-xs sf-pro-text font-medium text-gray-900">
                      {getCpfPaciente()}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Dados do Prescritor */}
          {getNomeMedico() && (
            <div className="p-4 sm:p-5 border-b border-gray-200/50">
              <h3 className="sf-pro-display font-semibold text-gray-900 mb-3 flex items-center space-x-2 text-sm">
                <Stethoscope className="w-4 h-4" />
                <span>Prescritor</span>
              </h3>
              <div className="glass-card-compact rounded-lg sm:rounded-xl p-3 sm:p-4">
                <div className="space-y-2">
                  <div className="flex justify-between items-start">
                    <span className="text-xs sf-pro-text text-gray-600">Nome:</span>
                    <span className="text-xs sf-pro-text font-semibold text-gray-900 text-right max-w-[60%] truncate">
                      {getNomeMedico()}
                    </span>
                  </div>
                  {getCrmMedico() && (
                    <div className="flex justify-between items-center">
                      <span className="text-xs sf-pro-text text-gray-600">CRM:</span>
                      <span className="text-xs sf-pro-text font-medium text-gray-900">
                        {getCrmMedico()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Medicamentos Dispensados */}
          <div className="p-4 sm:p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="sf-pro-display font-semibold text-gray-900 text-sm flex items-center space-x-2">
                <Pill className="w-4 h-4" />
                <span>Medicamentos Dispensados</span>
              </h3>
              <span className="glass-badge bg-gray-100/70 text-gray-700 border-gray-200/50 text-xs">
                {contarItens()} {contarItens() === 1 ? 'item' : 'itens'}
              </span>
            </div>
            
            <div className="space-y-2 max-h-64 sm:max-h-80 overflow-y-auto custom-scrollbar">
              {dispensacao.items && dispensacao.items.length > 0 ? (
                dispensacao.items.map((item, index) => (
                  <div key={item.id || index} className="glass-card-compact rounded-lg p-3">
                    <div className="space-y-2">
                      <div>
                        <span className="text-xs sf-pro-text text-gray-600">Medicamento:</span>
                        <p className="text-sm sf-pro-text font-semibold text-gray-900 mt-0.5">
                          {item.productName || item.name || item.nome || "Não informado"}
                        </p>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-2 text-xs sf-pro-text pt-2 border-t border-gray-200/50">
                        <div>
                          <span className="text-gray-600">Quantidade:</span>
                          <p className="font-medium text-gray-900 mt-0.5">
                            {item.quantity || item.quantidade || "—"}
                          </p>
                        </div>
                        {item.batch && (
                          <div>
                            <span className="text-gray-600">Lote:</span>
                            <p className="font-medium text-gray-900 mt-0.5">{item.batch}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-6">
                  <Pill className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="sf-pro-text text-xs text-gray-500">
                    {contarItens() > 0 
                      ? `${contarItens()} medicamento(s) dispensado(s)` 
                      : "Nenhum medicamento registrado"
                    }
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* ID da Prescrição */}
          {dispensacao.prescriptionId && (
            <div className="p-4 sm:p-5 border-t border-gray-200/50">
              <h3 className="sf-pro-display font-semibold text-gray-900 mb-3 text-sm flex items-center space-x-2">
                <Hash className="w-4 h-4" />
                <span>Identificação</span>
              </h3>
              <div className="glass-card-compact rounded-lg p-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs sf-pro-text text-gray-600">ID da Receita:</span>
                  <span className="text-xs sf-pro-text font-mono font-medium text-gray-900 break-all max-w-[60%] text-right">
                    {dispensacao.prescriptionId}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer com Botões */}
        <div className="p-4 sm:p-5 flex-shrink-0 border-t border-gray-200/50 bg-gray-50/30">
          <div className="flex gap-2">
            <button
              onClick={onFechar}
              className="px-4 py-2.5 bg-white/60 hover:bg-white/80 text-gray-600 hover:text-gray-800 rounded-lg border border-gray-200/60 transition-all duration-200 font-medium text-xs flex-1 sf-pro-text"
            >
              Fechar
            </button>
            <button
              onClick={handleDownload}
              className="px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg transition-all duration-200 font-medium text-xs flex items-center justify-center gap-1.5 flex-[2] sf-pro-text shadow-sm"
            >
              <Download className="w-3.5 h-3.5" />
              Baixar XML SNGPC
            </button>
          </div>
          
          {/* Compliance Badge */}
          <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mt-3">
            <Shield className="w-3.5 h-3.5" />
            <span className="sf-pro-text">XML SNGPC padrão ANVISA</span>
          </div>
        </div>
      </div>

      {/* Custom Styles */}
      <style>{`
        /* San Francisco Pro Font Stack */
        .sf-pro-display {
          font-family: -apple-system, "SF Pro Display", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        
        .sf-pro-text {
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
          font-feature-settings: "liga" 1, "kern" 1;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        /* Glass Morphism Components */
        .glass-card {
          background: rgba(255, 255, 255, 0.85);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.3);
          box-shadow: 
            0 8px 32px rgba(0, 0, 0, 0.1),
            inset 0 1px 0 rgba(255, 255, 255, 0.6);
        }

        .glass-card-compact {
          background: rgba(248, 250, 252, 0.6);
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          border: 1px solid rgba(226, 232, 240, 0.5);
          box-shadow: 
            0 1px 3px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.6);
        }

        /* Badge Styles */
        .glass-badge {
          padding: 3px 6px;
          border-radius: 6px;
          border: 1px solid;
          font-family: -apple-system, "SF Pro Text", BlinkMacSystemFont, sans-serif;
          font-size: 10px;
          font-weight: 500;
          backdrop-filter: blur(8px);
          -webkit-backdrop-filter: blur(8px);
          box-shadow: 
            0 1px 2px rgba(0, 0, 0, 0.05),
            inset 0 1px 0 rgba(255, 255, 255, 0.5);
        }

        /* Custom Scrollbar */
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: rgba(241, 245, 249, 0.5);
          border-radius: 4px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: rgba(203, 213, 225, 0.8);
          border-radius: 4px;
          transition: background 0.2s ease;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: rgba(148, 163, 184, 0.9);
        }

        /* Focus styles for accessibility */
        button:focus-visible {
          outline: 2px solid #10b981;
          outline-offset: 2px;
        }

        /* Mobile optimizations */
        @media (max-width: 640px) {
          .glass-card {
            border-radius: 1rem;
          }

          .glass-card-compact {
            border-radius: 0.5rem;
          }

          .custom-scrollbar {
            -webkit-overflow-scrolling: touch;
          }

          .custom-scrollbar::-webkit-scrollbar {
            width: 2px;
          }
        }

        /* High contrast mode support */
        @media (prefers-contrast: high) {
          .glass-card {
            background: rgba(255, 255, 255, 0.95);
            border: 2px solid rgba(0, 0, 0, 0.2);
          }

          .glass-card-compact {
            background: rgba(255, 255, 255, 0.9);
            border: 1px solid rgba(0, 0, 0, 0.3);
          }
        }

        /* Reduced motion support */
        @media (prefers-reduced-motion: reduce) {
          * {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }

        /* Prevent text selection on interactions */
        .glass-card,
        .glass-card-compact {
          user-select: text;
          -webkit-user-select: text;
        }
      `}</style>
    </div>,
    document.body
  );
};

export default ModalVisualizarDispensacao;